import { FieldJoined, ResponseError } from '@dropbox/sign-internal-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useSignerAppClient } from 'signer-app/context/signer-app-client';
import { extractSavedData } from 'signer-app/signer-experience/saved-data-utils';
import { SignatureRequestResponse } from 'signer-app/signer-experience/types';
import { useTouchField } from 'signer-app/signer-experience/use-validation';
import { SignatureRequest } from 'signer-app/signer-signature-document/types';
import { Field } from 'signer-app/types/editor-types';
import { useFeatureFlag } from 'js/sign-components/common/feature-flag';
import { useLatestRef } from 'js/sign-components/common/use-latest-ref';
import { signerExperienceQueryKey, type BackendErrorType } from './types';

export const SAVE_DELAY = 2000;

type UseFieldAutoSaveParams = {
  signatureRequestId: string;
  accessCode: string | null;
  signatureRequest: SignatureRequest | undefined;
};

export function useFieldAutoSave({
  signatureRequestId,
  accessCode,
  signatureRequest,
}: UseFieldAutoSaveParams) {
  const client = useSignerAppClient();
  const queryClient = useQueryClient();
  const touchField = useTouchField();
  const preserveOriginalRequired = useFeatureFlag(
    'sign_core_2026_02_19_fix_conditional_logic_original_required',
  );
  const [backendError, setBackendError] = React.useState<BackendErrorType>();

  const queryKey = signerExperienceQueryKey(signatureRequestId, accessCode);

  const fieldMutationKey = [signatureRequestId, accessCode, 'fields'] as const;

  const [draft, setDraft] = React.useState<Record<Field['id'], Field> | null>(
    null,
  );

  const { mutate: mutateFieldData, isLoading: isSavingFields } = useMutation<
    unknown,
    unknown,
    Record<Field['id'], Field>
  >({
    mutationKey: fieldMutationKey,
    mutationFn: async (updates) => {
      await queryClient.cancelQueries(fieldMutationKey);
      const data = queryClient.getQueryData<SignatureRequestResponse>(queryKey);
      if (data) {
        const fields = data.signatureRequest.fields.map(
          (f: FieldJoined): SignatureRequest['fields'][number] => {
            const saved = updates[f.id];
            return saved ?? f;
          },
        );
        queryClient.setQueryData(queryKey, {
          ...data,
          signatureRequest: {
            ...data.signatureRequest,
            fields,
          },
        });
        const savedData = fields.map((f) => [
          f.id,
          extractSavedData(f, { preserveOriginalRequired }),
        ]);
        await client.signerApi.signerAppSaveActionRequest({
          signatureRequestId,
          savedData: Object.fromEntries(savedData),
        });
        setDraft((prev) => {
          if (!prev) {
            return null;
          }

          const newDraft = { ...prev };
          Object.keys(updates).forEach((key) => {
            if (updates[key] === prev[key]) {
              delete newDraft[key];
            }
          });

          return Object.keys(newDraft).length > 0 ? newDraft : null;
        });
      }
    },
    onError: (error) => {
      const isResponseError = (error as ResponseError)?.response != null;
      if (isResponseError) {
        const status = (error as ResponseError).response.status;
        setBackendError(
          status === 401 || status === 403
            ? 'authentication-error'
            : 'generic-error',
        );
      } else {
        setBackendError('generic-error');
      }
    },
  });

  React.useEffect(() => {
    if (draft) {
      const timeout = setTimeout(() => {
        mutateFieldData(draft);
      }, SAVE_DELAY);
      return () => clearTimeout(timeout);
    }
    return () => {};
  }, [draft, mutateFieldData]);

  const onFieldUpdate = React.useCallback(
    <T extends Field>(field: T, updates: Partial<T>): void => {
      const updatedField: Field = { ...field, ...updates };
      touchField(field.id);
      setDraft((prev) => {
        return {
          ...prev,
          [field.id]: updatedField,
        };
      });
    },
    [touchField],
  );

  const flushDraft = React.useCallback(() => {
    if (draft) {
      mutateFieldData(draft);
      setDraft(null);
    }
  }, [draft, mutateFieldData]);

  const fields = React.useMemo((): SignatureRequest['fields'] => {
    const baseFields = signatureRequest?.fields;
    if (draft && baseFields) {
      return baseFields.map((field): FieldJoined => {
        const saved = draft[field.id];
        return saved ?? field;
      });
    }

    return baseFields ?? [];
  }, [draft, signatureRequest?.fields]);

  const isSavingRef = useLatestRef(isSavingFields);
  const draftRef = useLatestRef(draft);

  return {
    fields,
    onFieldUpdate,
    flushDraft,
    // TODO: Once sign_core_2026_03_02_fix_finalize_saving_race is fully rolled out,
    // remove isSavingFields and only expose isSavingRef. Then consider encapsulating
    // the wait-for-save logic (currently in useFinalize) into a single
    // `waitForSaveComplete()` function here instead of exposing raw refs.
    isSavingFields,
    isSavingRef,
    draftRef,
    backendError,
  };
}
